import {Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy, OnInit} from '@angular/core';
import {Event, NavigationEnd, NavigationStart, Router, RouterOutlet} from '@angular/router';
import {AuthenticationService} from '@datev/ngx-dcal';
import {AccountWizardHintRunner} from '@datev/accountwizard-hintdialog';
import {filter, of, Subscription, switchMap, take} from 'rxjs';
import {PageStateService} from '../shared/service/page-state/page-state.service';
import {AppStateService} from 'commons';
import {AdobeTrackingService} from '@datev/ngx-adobe-tracking';
import {OnboardingService} from '../shared/service/onboarding/onboarding.service';
import {AsyncPipe} from '@angular/common';
import {AgreementsComponent} from '@datev/mydatev-agreements';
import {FooterComponent} from './footer/footer.component';
import {DacklAlertComponent} from '@datev/ngx-dackl';
import {PersonalService} from '../shared/service/personal/personal.service';
import {catchError, map, startWith} from 'rxjs/operators';
import {AppLocalNavComponent} from './app-local-nav/app-local-nav.component';
import {AnalyticsFilterurlService} from '../shared/service/analytics/analytics.filterurl';
import {AppHeaderComponent} from './app-header/app-header.component';

import '@datev/dacks/layout';


@Component({
  selector: 'myupdates-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [DacklAlertComponent, FooterComponent, RouterOutlet,
    AgreementsComponent, AsyncPipe, AppLocalNavComponent, AppHeaderComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'myupdates-fe';
  isHomeRoute$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map((event: NavigationEnd) => event.urlAfterRedirects.startsWith('/delivery/all-items')),
    startWith(this.router.url.startsWith('/delivery/all-items'))
  );
  isNotProductChangeSelectionView$= this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map((event: NavigationEnd) => !event.urlAfterRedirects.includes('/product-change-selection')),
    startWith(!this.router.url.includes('/product-change-selection'))
  );

  private loginSubscription?: Subscription;
  private loginAdanSubscription?: Subscription;
  private routeSubscription?: Subscription;
  private businessPartnerHKNRsSubscription?: Subscription;
  private trackingStateSubscription?: Subscription;
  private accountWizardHintSubscription?: Subscription;

  // Info: PageStateService bleibt drin für die BrowserBack Funktionalität und weiteres Page Caching
  // Info: AppStateService wird auch im HTML benötigt zur Abfrage, daher protected und nicht private
  constructor(private readonly router: Router,
              private readonly adobeTrackingService: AdobeTrackingService,
              private readonly pageStateService: PageStateService, // NOSONAR
              protected readonly appState: AppStateService,
              private readonly personal: PersonalService,
              private readonly filterAnalyticsService: AnalyticsFilterurlService,
              private readonly onboarding: OnboardingService,
              private readonly auth: AuthenticationService,
              private readonly accountWizardHintRunner: AccountWizardHintRunner) {

    this.initAnalyticsService();
    this.auth.initialize().then(() => {
      this.syncAuthLogInState();
      this.trackInitialLogInState();
    });
    this.syncUserIsStillActive();
    this.syncTrackingServiceMode();
    this.syncActiveBusinessPartnerIdAndHKNRsAndPersonalFilterActivation();
  }

  ngOnInit(): void {
    this.manageOnboarding();
    this.initializeAccountWizardHint();
  }

  ngOnDestroy(): void {
    this.loginSubscription?.unsubscribe();
    this.loginAdanSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.businessPartnerHKNRsSubscription?.unsubscribe();
    this.trackingStateSubscription?.unsubscribe();
    this.accountWizardHintSubscription?.unsubscribe();
  }

  private manageOnboarding(): void {
    this.auth.initialize().then(() => {
      this.onboarding.removeOnboardingAgreements();
    });
  }

  private syncAuthLogInState(): void {
    this.loginSubscription = this.auth.isLoggedIn.subscribe(isLoggedIn => {
      if (this.appState.isPersonalized !== isLoggedIn) {
        this.appState.isPersonalized = isLoggedIn;
      }
    });
  }

  private trackInitialLogInState(): void {
    this.loginAdanSubscription = this.auth.isLoggedIn.pipe(take(1)).subscribe(isLoggedIn => {
      this.adobeTrackingService.publishClickEvent('loginState:StartAngular:' + (isLoggedIn ? 'personal' : 'public'));
    });
  }

  private syncUserIsStillActive(): void {
    this.routeSubscription = this.router.events.pipe(filter(ev => ev instanceof NavigationEnd))
      .subscribe(() => this.auth.userIsStillActive());
  }

  private syncTrackingServiceMode(): void {
    this.adobeTrackingService.activateTrackingInPublic();
    this.trackingStateSubscription = this.appState.isPersonalized$
      .subscribe(isPersonalized => {
        if (isPersonalized) {
          this.adobeTrackingService.activateTrackingInSecure();
        } else {
          this.adobeTrackingService.activateTrackingInPublic();
        }
    });
  }

  private syncActiveBusinessPartnerIdAndHKNRsAndPersonalFilterActivation(): void {
    this.businessPartnerHKNRsSubscription = this.appState.isPersonalized$.pipe(
      switchMap(isPersonalized => {
        if (isPersonalized) {
          return this.personal.activeBusinessPartner$.pipe(
            switchMap(activeBusinessPartner => {
              if (activeBusinessPartner.businessPartnerId > 0) {
                return this.personal.getBusinessPartnerFilterHKNRs(activeBusinessPartner.businessPartnerId).pipe(
                  map(businessPartnerHKNRs => ({
                    businessPartnerHKNRs,
                    businessPartnerId: activeBusinessPartner.businessPartnerId
                  }))
                );
              } else {
                return of({
                  businessPartnerHKNRs: undefined,
                  businessPartnerId: undefined
                });
              }
            }),
            catchError(() => of({
                businessPartnerHKNRs: undefined,
                businessPartnerId: undefined
              }))
          );
        } else {
          return of({
            businessPartnerHKNRs: undefined,
            businessPartnerId: undefined
          });
        }
      })
    ).subscribe(({ businessPartnerHKNRs, businessPartnerId }) => {
      if (businessPartnerId !== undefined || businessPartnerHKNRs !== undefined
        || this.appState.activeBusinessPartner.id !== undefined || this.appState.activeBusinessPartner.hknrs !== undefined) {
        this.appState.activeBusinessPartner = {id: businessPartnerId, hknrs: businessPartnerHKNRs};
      }

      // Sync PersonalFilterActivation
      this.appState.isPersonalProductChangeFilter = this.appState.isPersonalDeliveryFilter = !!businessPartnerHKNRs;
    });
  }

  private initAnalyticsService(): void {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        this.adobeTrackingService.publishPageReferrer(this.filterAnalyticsService.filterTrackingUrlParams(document.location.href));
      }
      if (event instanceof NavigationEnd) {
        this.adobeTrackingService.publishPageView(this.filterAnalyticsService.filterTrackingUrlParams(document.location.href));
      }
    });
  }

  /**
   * Initialisiert den AccountWizard Hint Dialog für die DATEV-Konto Migration.
   * Der Dialog wird nur angezeigt, wenn der Benutzer eingeloggt ist und
   * die Anmeldedaten (SmartCard/SmartLogin) noch nicht mit einem DATEV-Konto verknüpft sind.
   *
   * Hinweis: Der Dialog wird vom Backend gesteuert. Die Konfiguration muss
   * über das Account-Wizard-Launcher-Settings-Tool vorgenommen werden.
   * @see https://confluence.datev.de/spaces/DGP/pages/819033801/
   */
  private initializeAccountWizardHint(): void {
    this.accountWizardHintSubscription = this.auth.isLoggedIn.pipe(
      filter((isLoggedIn: boolean) => isLoggedIn),
      take(1)
    ).subscribe(() => {
      // Dialog wird nur gestartet, wenn der Benutzer eingeloggt ist
      this.accountWizardHintRunner.start();
    });
  }

}
